import {
    IExternalParameter,
    IExternalService,
    IExternalServiceDefaultFields,
    IExternalServiceParameter,
} from "../types/stores/flowControlData";
import { IEnrichmentField } from "../types/stores/umbrellaData";
import { FlowExternalServiceParameter } from "../types/stores/flowTypes";
import { UEFieldParms } from "./constants";

export const getUEPersonLayoutFieldData = (ueFields: Array<IEnrichmentField>): Array<IEnrichmentField> => {
    let finalFields: Array<IEnrichmentField> = [];
    ueFields.forEach(field => {
        const person = field.LevelId == 7 ? personFromFieldId(field.FieldId) : 1;
        const newField: IEnrichmentField = {
            FieldId: field.FieldId,
            FieldName: personFieldName(person, field.FieldName),
            ShortDescription: "",
            LevelId: field.LevelId,
            Length: field.Length,
            AlternateName: field.AlternateName ? personAlternateName(person, field.AlternateName?.toString()) : "",
        };
        finalFields.push(newField);
    });

    return finalFields;
};

export const getServiceFieldData = (
    serviceParameters: Array<IExternalServiceParameter>,
    itemParameters: Array<FlowExternalServiceParameter>,
    ueFields: Array<IEnrichmentField>,
    svcDefaultFields: Array<IExternalServiceDefaultFields>,
    externalServices: Array<IExternalService>,
    tableNameLength?: string
): Array<IEnrichmentField> => {
    const tableNameId = 9000000;

    const ueFieldParms = serviceParameters.filter(x => UEFieldParms.includes(x.ExternalParameter.ParameterName));
    const ueFieldParmIds = ueFieldParms.map(x => x.ExternalParameter.ParameterId);

    const parmsWithValues = itemParameters.filter(x => ueFieldParmIds.includes(x.ParameterId) && x.Value.length > 0);
    let svcDefaultFieldsValues: Array<IEnrichmentField> = [];
    const filteredDefaultFields = serviceDefaultFieldsFiltered(
        serviceParameters,
        itemParameters,
        svcDefaultFields,
        externalServices
    );
    filteredDefaultFields.forEach(item => {
        svcDefaultFieldsValues.push({
            FieldId: !isNaN(parseInt(item.FieldName)) ? parseInt(item.FieldName) : 0,
            FieldName: !isNaN(parseInt(item.FieldName)) ? item.DisplayName : item.FieldName,
            ShortDescription: item.DisplayName,
            Length: item.FieldLength,
            LevelId: item.SortOrder,
            AlternateName: "",
        });
    });

    let finalFields: Array<IEnrichmentField> = svcDefaultFieldsValues.length > 0 ? svcDefaultFieldsValues : [];
    let personFields: Array<IEnrichmentField> = [];

    parmsWithValues.forEach(parm => {
        const fieldIds = parm.Value.split(",");
        const parmName = ueFieldParms
            .filter(x => x.ParameterId == parm.ParameterId)
            .map(x => x.ExternalParameter.ParameterName)[0];
        const person = personNumber(parmName);

        fieldIds.forEach(id => {
            const ueField = ueFields.filter(x => x.FieldId == parseInt(id));

            if (ueField.length > 0) {
                const field: IEnrichmentField = {
                    FieldId: personFieldId(person, ueField[0]),
                    FieldName: personFieldName(person, ueField[0].FieldName),
                    ShortDescription: "",
                    LevelId: ueField[0].LevelId,
                    Length: ueField[0].Length,
                    AlternateName: ueField[0].AlternateName
                        ? personAlternateName(person, ueField[0].AlternateName?.toString())
                        : "",
                };
                personFields.push(field);
            }
        });
    });

    // creating Table Name Fields
    let tableNameFields: Array<IEnrichmentField> = [];
    personFields.forEach(item => {
        if (
            (tableNameLength == "2" || tableNameLength == "10") &&
            item.FieldId > 0 &&
            svcDefaultFields.filter(x => x.FieldName == item.FieldId.toString()).length == 0
        ) {
            const field: IEnrichmentField = {
                FieldId: item.FieldId,
                FieldName: (item.FieldId + tableNameId).toString(),
                ShortDescription: item.FieldName.toString().substring(0, 19),
                LevelId: item.LevelId,
                Length: parseInt(tableNameLength),
                AlternateName: "",
            };

            tableNameFields.push(field);
        }
    });

    // adding each Table Name Field after its Person Field
    tableNameFields.forEach(tableField => {
        personFields.splice(personFields.findIndex(x => x.FieldId == tableField.FieldId) + 1, 0, tableField);
    });

    // adding Person and Table Name fields to the rest of the fields
    personFields.forEach(field => finalFields.push(field));

    return finalFields;
};

const personNumber = (name: string) => {
    let person: string = "";
    switch (name) {
        case "Field_List":
            person = "1";
            break;
        default:
            person = name.substring(6, 7);
            break;
    }
    return parseInt(person);
};

const personFieldId = (person: number, field: IEnrichmentField) => {
    if (field.LevelId == 7 && person > 1) {
        const realId = field.FieldId % 100000000;
        return person * 100000000 + realId;
    } else {
        return field.FieldId;
    }
};

const personFieldName = (person: number, name: string) => {
    if (name.toLocaleLowerCase().startsWith("person #:")) {
        const newPerson = person.toString() + ":";
        return name.replace("#:", newPerson);
    } else {
        return name;
    }
};

const personAlternateName = (person: number, name: string) => {
    if (person > 1) {
        if (name.startsWith("Person_")) {
            return name + "_" + person.toString();
        } else if (name.lastIndexOf("_") > 0) {
            const index = name.lastIndexOf("_");
            return name.substring(0, index + 1) + person.toString();
        } else {
            return name;
        }
    } else {
        return name;
    }
};

const personFromFieldId = (fieldId: number): number => {
    if (fieldId > 100000000) {
        return parseInt(fieldId.toString().substring(0, 1));
    } else {
        return 1;
    }
};

const isServiceParameterSelected = (
    serviceParameters: Array<IExternalServiceParameter>,
    itemParameters: Array<FlowExternalServiceParameter>,
    parameterName: string
): boolean => {
    const returnMmc = serviceParameters.find(x => x.ExternalParameter.ParameterName == parameterName);

    if (returnMmc) {
        const itemParm = itemParameters.find(x => x.ParameterId == returnMmc.ParameterId);
        if (itemParm?.Value == "true") {
            return true;
        }
    }

    return false;
};

const serviceDefaultFieldsFiltered = (
    allParameters: Array<IExternalServiceParameter>,
    itemParameters: Array<FlowExternalServiceParameter>,
    svcDefaultFields: Array<IExternalServiceDefaultFields>,
    externalServices: Array<IExternalService>
): Array<IExternalServiceDefaultFields> => {
    let serviceFields: Array<IExternalServiceDefaultFields> = [];
    const serviceIds = [...new Set(svcDefaultFields.map(x => x.ServiceFieldId))];
    const serviceParameters = allParameters.filter(x => serviceIds.includes(x.ServiceId));
    svcDefaultFields.forEach(item => {
        // Service specific filter of fields
        const fieldService = externalServices.find(x => x.ServiceId == item.ServiceFieldId);
        if (fieldService && filterServices.includes(fieldService.ServiceName)) {
            switch (fieldService.ServiceName) {
                // MatchMaker 1.0
                case "match_maker_legacy": {
                    switch (item.FieldName) {
                        case "MatchMaker_Code": {
                            if (!isServiceParameterSelected(serviceParameters, itemParameters, "return_mmc")) return;

                            break;
                        }
                        case "MatchMaker_Code_Dec": {
                            if (!isServiceParameterSelected(serviceParameters, itemParameters, "return_mmc_dec"))
                                return;

                            break;
                        }
                        default: {
                            if (!isServiceParameterSelected(serviceParameters, itemParameters, "matchback_type"))
                                return;

                            break;
                        }
                    }

                    break;
                }

                // MatchMaker 2.0
                case "match_maker": {
                    const addressParm = serviceParameters.find(
                        x => x.ExternalParameter.ParameterName == "Address_Match_Types"
                    );

                    if (addressParm && mm2FilterFields.includes(item.FieldName)) {
                        const itemParm = itemParameters.find(x => x.ParameterId == addressParm.ParameterId);
                        if (itemParm) {
                            const parmValues: Array<string> = itemParm.Value.split(",");
                            if (
                                (item.FieldName == "postal_address_hash" && !parmValues.includes("P")) ||
                                (item.FieldName == "moderate_postal_address_hash" && !parmValues.includes("O")) ||
                                (item.FieldName == "lenient_postal_address_hash" && !parmValues.includes("N"))
                            ) {
                                return;
                            }
                        }
                    }

                    break;
                }
            }
        }

        serviceFields.push(item);
    });

    return serviceFields;
};

interface IFlowServiceParameterSort {
    FlowServiceParameterId: number;
    FlowItemId: number;
    ServiceId: number;
    ParameterId: number;
    Value: string;
    SortOrder: number;
}

export const getSortedServiceParameters = (
    serviceId: number,
    externalServiceParameters: Array<IExternalServiceParameter>,
    flowServiceParameters: Array<FlowExternalServiceParameter>
): Array<FlowExternalServiceParameter> => {
    const serviceParameters = externalServiceParameters.filter(x => x.ServiceId == serviceId);
    if (!serviceParameters || serviceParameters.length == 0) {
        return flowServiceParameters;
    }

    let parmsWithSort: Array<IFlowServiceParameterSort> = [];
    flowServiceParameters.forEach(flowParm => {
        const svcParm = serviceParameters.filter(x => x.ParameterId == flowParm.ParameterId);
        const newParm: IFlowServiceParameterSort = {
            FlowServiceParameterId: flowParm.FlowServiceParameterId,
            FlowItemId: flowParm.FlowItemId,
            ServiceId: flowParm.ServiceId,
            ParameterId: flowParm.ParameterId,
            Value: flowParm.Value,
            SortOrder: svcParm.length == 1 ? svcParm[0].SortOrder : 99,
        };
        parmsWithSort.push(newParm);
    });

    parmsWithSort.sort((a, b) => (a.SortOrder > b.SortOrder ? 1 : -1));
    let finalParms: Array<FlowExternalServiceParameter> = [];
    parmsWithSort.forEach(x => {
        const finalParm: FlowExternalServiceParameter = {
            FlowServiceParameterId: x.FlowServiceParameterId,
            FlowItemId: x.FlowItemId,
            ServiceId: x.ServiceId,
            ParameterId: x.ParameterId,
            Value: x.Value,
        };
        finalParms.push(finalParm);
    });

    return finalParms;
};

export const getItemParameterValue = (
    parameters: Array<IExternalParameter>,
    flowExternalServiceParameters: Array<FlowExternalServiceParameter>,
    name: string
): string => {
    // get id
    const id = parameters.find(parameter => parameter.ParameterName == name)?.ParameterId;
    if (flowExternalServiceParameters.filter(parameter => parameter.ParameterId == id).length > 0) {
        return flowExternalServiceParameters.filter(parameter => parameter.ParameterId == id)[0].Value || "";
    } else {
        return "";
    }
};

const filterServices: Array<string> = ["match_maker", "ue", "let", "match_maker_legacy"];
const mm2FilterFields = ["postal_address_hash", "moderate_postal_address_hash", "lenient_postal_address_hash"];
